import { Card, CardContent, CardHeader } from '@/shared/ui/card'
import { Skeleton } from '@/shared/ui/skeleton'

/**
 * The pending state for public routes. The default is `AppShellSkeleton`, which
 * draws app chrome — wrong on a page the visitor has not signed in to.
 *
 * It is the sign-in card's own geometry rather than a spinner, for the reason
 * the shell skeletons are: a placeholder has the shape of what replaces it.
 *
 * Every number is copied from what replaces it and none of them are round:
 * the title is `text-h3` at `leading-snug` — 15 × 1.375 = 20.6 — and every
 * caption line is `text-caption` at the inherited `leading-text`, 12 × 1.55 =
 * 18.6. Drawn as 24 and 16 the card came out 39px short, because the link line
 * under the form was missing as well.
 *
 * The `.boot--auth` markup in `index.html` is the same drawing in plain CSS and
 * paints before any of this loads. The two are a pair and change together —
 * `shell-geometry.test.ts` fails if one moves without the other.
 */
export function AuthPending({ hasFieldHints = false }: { hasFieldHints?: boolean }) {
  return (
    <Card className="w-modal-confirm max-w-full">
      <CardHeader>
        <Skeleton className="h-5.25 w-40" />
        <Skeleton className="h-4.75 w-56" />
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {/* The form is its own `gap-4` block inside the content's `gap-5`, the
            way both forms are: the link below them is not a form row. */}
        <div className="flex flex-col gap-4">
          <PendingField hasHint={hasFieldHints} />
          <PendingField hasHint={hasFieldHints} />
          <Skeleton className="h-control-md" />
        </div>

        <Skeleton className="h-4.75 w-48" />
      </CardContent>
    </Card>
  )
}

/**
 * `FloatingField`'s own `flex flex-col gap-1.5`: the 44px control, and under it
 * the caption that carries a hint.
 *
 * Sign-in shows a hint only on an error, so its fields are bare. Registration
 * always shows two — the username rule and the password length — which is 50px
 * of card the sign-in drawing does not have, and the reason this is a prop.
 */
function PendingField({ hasHint }: { hasHint: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-control-lg" />
      {hasHint && <Skeleton className="h-4.75 w-40" />}
    </div>
  )
}
